/** Shared "company identity + address" field group (Campaigns/Sessions v2).
 *
 * Rendered identically on the beneficiary form (CampaignLeads → NewLeadModal,
 * item #13) and the session participant form (CreateSessionButton, item #14),
 * so an operator captures the same country-aware company details in both
 * places. The registration-number label follows the address country
 * (SIREN/SIRET in FR, CIF/NIF in ES, …) via the shared registrationLabelKey.
 *
 * Purely controlled: the parent owns a `PartyDetails` slice of its form
 * state and applies the emitted patch. Every field is optional.
 */
import { useTranslation } from "react-i18next";

import { CountrySelector } from "@/components/CountrySelector";
import type { PartyDetails } from "@/lib/party";
import { registrationLabelKey } from "@/lib/registration";

export function CompanyIdentityFields({
  value,
  onChange,
  idPrefix = "party",
  showAddress = true,
  legalNameRequired = false,
}: {
  value: PartyDetails;
  onChange: (patch: Partial<PartyDetails>) => void;
  idPrefix?: string;
  /** When false, renders only the company identity (legal name / reg# / VAT)
   *  and omits the address block — used where the address is captured as a
   *  basic beneficiary field instead of a company-scoped one. */
  showAddress?: boolean;
  /** Marks the legal-name label with a required asterisk — set by callers
   *  (BeneficiaryFormFields) where a legal entity's identity can rest on
   *  the company name alone. Every other caller of this shared component
   *  keeps every field optional (unchanged default). */
  legalNameRequired?: boolean;
}) {
  const { t } = useTranslation();
  const set =
    (k: keyof PartyDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ [k]: e.target.value });
    };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-nb-text-3">
        {showAddress
          ? t("party.section")
          : t("party.companySection", "Company information")}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="label" htmlFor={`${idPrefix}-legal_name`}>
            {t("party.legalName")}
            {legalNameRequired && (
              <span className="ml-0.5 text-cvp-danger">*</span>
            )}
          </label>
          <input
            id={`${idPrefix}-legal_name`}
            className="input"
            value={value.legal_name}
            placeholder={t("party.legalNamePlaceholder")}
            onChange={set("legal_name")}
          />
        </div>
        <div>
          <label className="label" htmlFor={`${idPrefix}-registration_number`}>
            {t(registrationLabelKey(value.country))}
          </label>
          <input
            id={`${idPrefix}-registration_number`}
            className="input"
            value={value.registration_number}
            onChange={set("registration_number")}
          />
        </div>
        <div>
          <label className="label" htmlFor={`${idPrefix}-vat_id`}>
            {t("party.vatId")}
          </label>
          <input
            id={`${idPrefix}-vat_id`}
            className="input"
            value={value.vat_id}
            placeholder="FR12345678901"
            onChange={set("vat_id")}
          />
        </div>
        {showAddress && (
          <>
            <div className="col-span-2">
              <label className="label" htmlFor={`${idPrefix}-address_line`}>
                {t("party.addressLine")}
              </label>
              <input
                id={`${idPrefix}-address_line`}
                className="input"
                value={value.address_line}
                onChange={set("address_line")}
              />
            </div>
            <div>
              <label className="label" htmlFor={`${idPrefix}-postal_code`}>
                {t("party.postalCode")}
              </label>
              <input
                id={`${idPrefix}-postal_code`}
                className="input"
                value={value.postal_code}
                onChange={set("postal_code")}
              />
            </div>
            <div>
              <label className="label" htmlFor={`${idPrefix}-city`}>
                {t("party.city")}
              </label>
              <input
                id={`${idPrefix}-city`}
                className="input"
                value={value.city}
                onChange={set("city")}
              />
            </div>
            <div>
              <label className="label" htmlFor={`${idPrefix}-country`}>
                {t("party.country")}
              </label>
              <CountrySelector
                id={`${idPrefix}-country`}
                value={value.country}
                onChange={(code) => onChange({ country: code })}
                allowEmpty
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
